import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { AdReach } from "@/components/AdReach";
import { Products } from "@/components/Products";
import { StatsStrip } from "@/components/StatsStrip";
import { Manufacturing } from "@/components/Manufacturing";
import { Sustainability } from "@/components/Sustainability";
import { Testimonials } from "@/components/Testimonials";
import { QuoteSection } from "@/components/QuoteSection";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { QuoteModal } from "@/components/QuoteModal";
import { AboutPage } from "@/components/AboutPage";
import { PrivacyPage } from "@/components/PrivacyPage";

const Landing = ({ onQuote, onNav, presetType }) => {
  const [journey, setJourney] = useState("advertiser");
  const goContact = (j) => {
    setJourney(j);
    onNav("#contact");
  };
  return (
    <main>
      <Hero onQuote={onQuote} onNav={onNav} onAdvertise={() => goContact("advertiser")} onVendor={() => goContact("vendor")} />
      <Marquee />
      <Products onQuote={onQuote} />
      <StatsStrip />
      <AdReach />
      <Manufacturing />
      <Sustainability />
      <Testimonials />
      <QuoteSection presetType={presetType} />
      <Contact journey={journey} setJourney={setJourney} />
    </main>
  );
};

function AppShell() {
  const lenisRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [presetType, setPresetType] = useState(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const scrollToEl = useCallback((href) => {
    const el = document.querySelector(href === "#top" ? "#top" : href);
    if (el && lenisRef.current) {
      lenisRef.current.scrollTo(el, { offset: -70, duration: 1.4 });
    }
  }, []);

  const onNav = useCallback(
    (href) => {
      if (href.startsWith("/")) {
        navigate(href);
        lenisRef.current?.scrollTo(0, { immediate: true });
        return;
      }
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToEl(href), 500);
      } else {
        scrollToEl(href);
      }
    },
    [location.pathname, navigate, scrollToEl]
  );

  const openQuote = useCallback((type) => {
    setPresetType(type || null);
    setQuoteOpen(true);
  }, []);

  return (
    <div className="App bg-[#FAF7F2]">
      <Toaster position="top-center" richColors />
      <Navbar onQuote={openQuote} onNav={onNav} />
      <Routes>
        <Route path="/" element={<Landing onQuote={openQuote} onNav={onNav} presetType={presetType} />} />
        <Route path="/about" element={<AboutPage onQuote={openQuote} />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
      <Footer onNav={onNav} />
      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} presetType={presetType} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
